/**
 * Role-Based Sidebar Navigation for CamPassed
 * Reads permissions from permissions_config table via API
 */

import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/context/PermissionsContext';
import { usePermissions as useLocalPermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';
import {
  Calendar,
  Plus,
  Users,
  QrCode,
  CreditCard,
  Shield,
  BarChart3,
  UserCircle,
  UserPlus,
  DollarSign,
  FileText,
  Megaphone,
  GraduationCap,
  Wallet,
  LayoutDashboard,
  CalendarCheck,
  Ticket,
  Search,
  Receipt,
  User,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertTriangle,
  HelpCircle,
  Settings,
  LogOut,
  Menu,
  School,
  Building2,
  CalendarDays,
  Users2,
  LifeBuoy,
  Settings2,
  Award,
  LayoutGrid,
  Sparkles,
  Crown,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import OrganizationSwitcher from '@/components/OrganizationSwitcher';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  Calendar,
  Plus,
  Users,
  QrCode,
  CreditCard,
  Shield,
  BarChart3,
  UserCircle,
  UserPlus,
  DollarSign,
  FileText,
  Megaphone,
  GraduationCap,
  Wallet,
  LayoutDashboard,
  CalendarCheck,
  Ticket,
  Search,
  Receipt,
  User,
  School,
  Building2,
  CalendarDays,
  Users2,
  LifeBuoy,
  Settings2,
  Award,
  LayoutGrid,
  Settings,
  HelpCircle,
  Crown,
};

// Platform navigation items (static - platform admins have full access)
const platformNavItems = [
  { name: "Dashboard", path: "/platform", icon: BarChart3 },
  { name: "Campuses", path: "/platform/campuses", icon: School },
  { name: "Organizations", path: "/platform/organizations", icon: Building2 },
  { name: "Events", path: "/platform/events", icon: CalendarDays },
  { name: "Monetization", path: "/platform/monetization", icon: Wallet },
  { name: "Sponsors", path: "/platform/sponsors", icon: Award },
  { name: "Ads", path: "/platform/ads", icon: Megaphone },
  { name: "Placements", path: "/platform/placements", icon: LayoutGrid },
  { name: "Revenue", path: "/platform/revenue", icon: DollarSign },
  // User Management Section
  { name: "divider", label: "User Management" },
  { name: "All Users", path: "/platform/users", icon: Users2 },
  { name: "Presidents", path: "/platform/presidents", icon: Crown },
  // Support Section
  { name: "divider", label: "Support" },
  { name: "Support", path: "/platform/support", icon: LifeBuoy },
  { name: "Settings", path: "/platform/settings", icon: Settings2 },
  { name: "Help", path: "/platform/help", icon: HelpCircle },
];

// Navigation Item Component
const NavItem = ({ item, isActive, collapsed, onClick }) => {
  const IconComponent = iconMap[item.icon] || Calendar;
  
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
        "text-sm font-medium",
        isActive
          ? "bg-electric-ice-blue/10 text-electric-ice-blue"
          : "text-soft-silver hover:bg-white/[0.05] hover:text-cloud-white"
      )}
    >
      <IconComponent className={cn("w-5 h-5 flex-shrink-0", isActive && "text-electric-ice-blue")} />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </Link>
  );
};

// Navigation Section Component
const NavSection = ({ section, currentPath, collapsed, onNavClick }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!section.items || section.items.length === 0) return null;

  return (
    <div className="mb-6">
      {/* Section Header */}
      {!collapsed && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-text hover:text-soft-silver transition-colors"
        >
          <span>{section.label}</span>
          {isExpanded ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
        </button>
      )}

      {/* Section Items */}
      {(isExpanded || collapsed) && (
        <div className="space-y-1">
          {section.items.map((item) => (
            <NavItem
              key={item.key}
              item={item}
              isActive={currentPath === item.path || currentPath.startsWith(item.path + '/')}
              collapsed={collapsed}
              onClick={onNavClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Door Staff Limited View
const DoorStaffView = ({ event, currentPath, onNavClick }) => {
  if (!event) {
    return (
      <div className="px-4 py-8 text-center">
        <AlertTriangle className="w-8 h-8 text-soft-amber mx-auto mb-3" />
        <p className="text-soft-silver text-sm">No active event assignment</p>
        <p className="text-muted-text text-xs mt-1">Contact an admin to be assigned to an event</p>
      </div>
    );
  }

  const doorStaffItems = [
    {
      key: 'door_staff_scanner',
      label: 'Event Scanner',
      path: '/door-staff',
      icon: 'QrCode'
    }
  ];

  return (
    <div>
      {/* Event Badge */}
      <div className="px-4 py-3 mb-4 bg-electric-ice-blue/5 border border-electric-ice-blue/20 rounded-xl mx-3">
        <p className="text-xs text-electric-ice-blue font-medium">ASSIGNED EVENT</p>
        <p className="text-cloud-white font-semibold mt-1 truncate">{event.event_name}</p>
      </div>

      {/* Door Staff Nav */}
      <div className="px-3 space-y-1">
        {doorStaffItems.map((item) => (
          <NavItem
            key={item.key}
            item={item}
            isActive={currentPath.startsWith(item.path)}
            onClick={onNavClick}
          />
        ))}
      </div>

      {/* Permissions Info */}
      <div className="px-4 py-3 mt-4 mx-3 bg-graphite rounded-lg">
        <p className="text-xs text-muted-text mb-2">Your Permissions:</p>
        <div className="space-y-1 text-xs">
          {event.can_check_in && (
            <div className="flex items-center gap-2 text-cool-mint">
              <QrCode className="w-3 h-3" />
              <span>Check-in guests</span>
            </div>
          )}
          {event.can_verify_age && (
            <div className="flex items-center gap-2 text-cool-mint">
              <Shield className="w-3 h-3" />
              <span>Verify age</span>
            </div>
          )}
          {event.can_deny_entry && (
            <div className="flex items-center gap-2 text-coral-red">
              <AlertTriangle className="w-3 h-3" />
              <span>Deny entry</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Platform Nav Item Component (simpler - no permission key, just icon components)
const PlatformNavItem = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      onClick={onClick}
      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
      className={cn(
        "flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200 rounded-md mx-2 group",
        isActive
          ? "bg-electric-ice-blue/10 text-electric-ice-blue"
          : "text-soft-silver hover:bg-white/[0.03] hover:text-cloud-white hover:translate-x-0.5"
      )}
    >
      <Icon className={cn(
        "w-5 h-5 flex-shrink-0 transition-transform duration-200",
        !isActive && "group-hover:scale-110"
      )} />
      <span className="tracking-[-0.01em]">{item.name}</span>
    </Link>
  );
};

// Fallback navigation items when API fails (for org variant)
const fallbackOrgNavItems = [
  { name: "Command Center", path: "/app/command-center", icon: LayoutDashboard },
  { name: "Events", path: "/app/events", icon: Calendar },
  { name: "Guests", path: "/app/guests", icon: Users },
  { name: "Scanner", path: "/app/scanner", icon: QrCode },
  { name: "Members", path: "/app/members", icon: UserCircle },
  { name: "Alumni", path: "/app/alumni", icon: GraduationCap },
  { name: "Dues", path: "/app/dues", icon: DollarSign },
  { name: "Documents", path: "/app/documents", icon: FileText },
  { name: "Announcements", path: "/app/announcements", icon: Megaphone },
  { name: "Recruitment", path: "/app/recruitment", icon: UserPlus },
  { name: "Analytics", path: "/app/analytics", icon: BarChart3, roles: ['owner', 'admin', 'event_manager'] },
  { name: "Sponsors", path: "/app/sponsors", icon: Award },
  { name: "Campaigns", path: "/app/campaigns", icon: Megaphone },
  { name: "Monetization", path: "/app/monetization", icon: Wallet },
  { name: "Settings", path: "/app/settings", icon: Settings, roles: ['owner'] },
  { name: "Billing", path: "/app/billing", icon: CreditCard },
  { name: "Help", path: "/app/help", icon: HelpCircle },
];

// Sidebar Content Component (shared between desktop and mobile)
const SidebarContent = ({ variant, collapsed, onNavClick, onAIAssistantClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, organization, signOut, session, profile } = useAuth();
  const { isDoorStaff } = useLocalPermissions();
  
  // State for active ticket check
  const [hasActiveTicket, setHasActiveTicket] = useState(false);
  const [activeTicketId, setActiveTicketId] = useState(null);
  
  // Only use context permissions for org variant
  const contextPermissions = usePermissions();
  const { 
    role, 
    navigation, 
    isDoorStaffOnly, 
    doorStaffEvent, 
    loading,
    error,
    getRoleDisplayName 
  } = contextPermissions;
  
  // Check for active tickets (for "My Ticket & QR" link)
  useEffect(() => {
    const checkActiveTicket = async () => {
      if (variant !== 'org' || !session?.access_token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/tickets/has-active-ticket`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setHasActiveTicket(data.has_ticket);
          
          // If has ticket, get the first ticket's ID for direct navigation
          if (data.has_ticket && data.count > 0) {
            const ticketsResponse = await fetch(`${API_URL}/api/tickets/my-tickets`, {
              headers: { Authorization: `Bearer ${session.access_token}` }
            });
            if (ticketsResponse.ok) {
              const ticketsData = await ticketsResponse.json();
              if (ticketsData.tickets?.length > 0) {
                setActiveTicketId(ticketsData.tickets[0].id);
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to check active tickets:', err);
      }
    };
    
    checkActiveTicket();
  }, [variant, session?.access_token]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const isPlatform = variant === 'platform';
  const homePath = isPlatform ? '/platform' : '/app/command-center';
  const helpPath = isPlatform ? '/platform/help' : '/app/help';
  const profilePath = isPlatform ? '/platform/profile' : '/app/profile';
  const settingsPath = isPlatform ? '/platform/settings' : '/app/settings';

  // Determine if we should use fallback navigation
  const hasNavigation = navigation?.sections?.length > 0;
  const shouldUseFallback = !isPlatform && !loading && !hasNavigation && !isDoorStaffOnly;

  return (
    <div className="flex flex-col h-full bg-obsidian border-r border-white/[0.08]">
      {/* Logo */}
      <div className="px-6 py-6 border-b border-white/[0.08]">
        <Link to={homePath} className="flex items-center gap-3 group">
          <img 
            src="/assets/app-icon.png" 
            alt="CamPassed" 
            className="w-8 h-8 rounded-lg transition-transform duration-200 group-hover:scale-105 shadow-lg shadow-electric-ice-blue/20"
          />
          {!collapsed && (
            <span className="text-xl font-bold tracking-[-0.02em] text-cloud-white">
              CamPassed
            </span>
          )}
        </Link>
      </div>

      {/* Organization Switcher (only for org variant) */}
      {variant === 'org' && !collapsed && (
        <div className="px-2 py-3 border-b border-white/[0.08]">
          <OrganizationSwitcher />
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {/* My Ticket & QR - shown only when user has active ticket */}
        {!isPlatform && hasActiveTicket && (
          <div className="mb-4">
            <Link
              to={activeTicketId ? `/app/my-ticket/${activeTicketId}` : '/app/my-tickets'}
              onClick={onNavClick}
              data-testid="nav-my-ticket"
              className={cn(
                "flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200 rounded-md mx-2 group",
                "bg-gradient-to-r from-electric-ice-blue/10 to-cool-mint/10 border border-electric-ice-blue/20",
                (location.pathname.includes('/my-ticket') || location.pathname === '/app/my-tickets')
                  ? "text-electric-ice-blue"
                  : "text-cloud-white hover:text-electric-ice-blue"
              )}
            >
              <div className="w-8 h-8 rounded-lg bg-electric-ice-blue/20 flex items-center justify-center">
                <Ticket className="w-4 h-4 text-electric-ice-blue" />
              </div>
              <span className="font-medium">My Ticket & QR</span>
            </Link>
          </div>
        )}
        
        {isPlatform ? (
          // Platform navigation - static items with dividers
          <div className="space-y-1">
            {platformNavItems.map((item, idx) => (
              item.name === 'divider' ? (
                <div key={`divider-${idx}`} className="py-3 px-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-text">
                    {item.label}
                  </p>
                </div>
              ) : (
                <PlatformNavItem
                  key={item.path}
                  item={item}
                  isActive={location.pathname === item.path || (item.path !== "/platform" && location.pathname.startsWith(item.path))}
                  onClick={onNavClick}
                />
              )
            ))}
          </div>
        ) : loading ? (
          // Loading state for org navigation
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-electric-ice-blue animate-spin" />
          </div>
        ) : isDoorStaffOnly ? (
          // Door staff limited view
          <DoorStaffView 
            event={doorStaffEvent} 
            currentPath={location.pathname}
            onNavClick={onNavClick}
          />
        ) : shouldUseFallback ? (
          // Fallback navigation when API fails or returns empty
          <>
            <div className="space-y-1">
              {fallbackOrgNavItems
                .filter(item => {
                  // If item has role restrictions, check if user has required role
                  if (item.roles) {
                    return item.roles.includes(role);
                  }
                  return true;
                })
                .map((item) => (
                <PlatformNavItem
                  key={item.path}
                  item={item}
                  isActive={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
                  onClick={onNavClick}
                />
              ))}
            </div>
            
            {/* AI Assistant for fallback navigation - check profile.platform_role */}
            {(profile?.platform_role === 'platform_owner' || profile?.platform_role === 'platform_admin') && onAIAssistantClick && (
              <div className="mt-4 pt-4 border-t border-white/[0.06] mx-3">
                <button
                  onClick={onAIAssistantClick}
                  data-testid="nav-ai-assistant"
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm font-medium w-full",
                    "bg-gradient-to-r from-purple-500/10 to-electric-ice-blue/10 border border-purple-500/20",
                    "text-purple-300 hover:text-purple-200 hover:border-purple-500/40"
                  )}
                >
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-purple-500 to-electric-ice-blue flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                  {!collapsed && <span>AI Assistant</span>}
                </button>
              </div>
            )}
          </>
        ) : (
          // Role-based navigation sections from API
          <>
            {navigation.sections?.map((section, idx) => (
              <NavSection
                key={idx}
                section={section}
                currentPath={location.pathname}
                collapsed={collapsed}
                onNavClick={onNavClick}
              />
            ))}
            
            {/* Owner-only settings links */}
            {role === 'owner' && (
              <div className="mt-4 pt-4 border-t border-white/[0.06] mx-3">
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-text">
                  Settings
                </div>
                <div className="space-y-1">
                  <Link
                    to="/app/permission-settings"
                    onClick={onNavClick}
                    data-testid="nav-permission-settings"
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm font-medium",
                      location.pathname === '/app/permission-settings'
                        ? "bg-electric-ice-blue/10 text-electric-ice-blue"
                        : "text-soft-silver hover:bg-white/[0.05] hover:text-cloud-white"
                    )}
                  >
                    <Shield className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span>Permission Settings</span>}
                  </Link>
                  <Link
                    to="/app/chapter-settings"
                    onClick={onNavClick}
                    data-testid="nav-chapter-settings"
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm font-medium",
                      location.pathname === '/app/chapter-settings'
                        ? "bg-electric-ice-blue/10 text-electric-ice-blue"
                        : "text-soft-silver hover:bg-white/[0.05] hover:text-cloud-white"
                    )}
                  >
                    <Building2 className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span>Chapter Settings</span>}
                  </Link>
                </div>
              </div>
            )}

            {/* AI Assistant - for owner, admin roles, and platform admins */}
            {(profile?.platform_role === 'platform_owner' || profile?.platform_role === 'platform_admin' || role === 'owner' || role === 'admin') && onAIAssistantClick && (
              <div className="mt-4 pt-4 border-t border-white/[0.06] mx-3">
                <button
                  onClick={onAIAssistantClick}
                  data-testid="nav-ai-assistant"
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm font-medium w-full",
                    "bg-gradient-to-r from-purple-500/10 to-electric-ice-blue/10 border border-purple-500/20",
                    "text-purple-300 hover:text-purple-200 hover:border-purple-500/40"
                  )}
                >
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-purple-500 to-electric-ice-blue flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                  {!collapsed && <span>AI Assistant</span>}
                </button>
              </div>
            )}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-white/[0.08] p-4">
        {/* Help Link */}
        <Link
          to={helpPath}
          onClick={onNavClick}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-soft-silver hover:bg-white/[0.05] hover:text-cloud-white transition-colors mb-2"
        >
          <HelpCircle className="w-5 h-5" />
          {!collapsed && <span className="text-sm">Help & Support</span>}
        </Link>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full">
            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.05] transition-colors">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-electric-ice-blue to-cool-mint flex items-center justify-center text-obsidian font-semibold text-sm">
                {user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
              {!collapsed && (
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-cloud-white truncate">
                    {user?.email?.split('@')[0] || 'User'}
                  </p>
                  <p className="text-xs text-muted-text truncate">{user?.email}</p>
                </div>
              )}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-graphite border-white/[0.08]">
            <DropdownMenuItem asChild>
              <Link to={profilePath} className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>My Profile</span>
              </Link>
            </DropdownMenuItem>
            {(isPlatform || role === 'owner' || role === 'admin') && (
              <DropdownMenuItem asChild>
                <Link to={settingsPath} className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-white/[0.08]" />
            <DropdownMenuItem onClick={handleSignOut} className="text-coral-red focus:text-coral-red">
              <LogOut className="w-4 h-4 mr-2" />
              <span>Sign Out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* Footer Label */}
        <div className="pt-3 text-xs text-muted-text text-center">
          {isDoorStaff && variant === 'org'
            ? "Door Staff Mode" 
            : variant === "org" 
              ? "Organization Portal" 
              : "Platform Admin"
          }
        </div>
      </div>
    </div>
  );
};

// Main Sidebar Component with Mobile Support
export const RoleBasedSidebar = ({ variant = "org", onAIAssistantClick }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  
  return (
    <>
      {/* Desktop Sidebar */}
      <aside 
        className="hidden lg:flex flex-col w-[240px] h-screen fixed left-0 top-0 bg-obsidian border-r border-white/[0.08] z-40"
        data-testid={`${variant}-sidebar`}
      >
        <SidebarContent variant={variant} onAIAssistantClick={onAIAssistantClick} />
      </aside>
      
      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden fixed top-4 left-4 z-50 text-cloud-white hover:bg-white/[0.05]"
            data-testid="mobile-menu-trigger"
          >
            <Menu className="w-6 h-6" />
          </Button>
        </SheetTrigger>
        <SheetContent 
          side="left" 
          className="w-[280px] sm:w-[240px] p-0 bg-obsidian border-r border-white/[0.08]"
        >
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <SidebarContent 
            variant={variant}
            onNavClick={() => setMobileOpen(false)}
            onAIAssistantClick={() => {
              setMobileOpen(false);
              onAIAssistantClick?.();
            }}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default RoleBasedSidebar;
